<template>
  <div class="change-logs">
    <table class="cl-table">
      <colgroup>
        <col style="width: 63px" />   <!-- Serial -->
        <col style="width: 150px" />  <!-- Date -->
        <col style="width: 120px" />  <!-- User -->
        <col style="width: 120px" />  <!-- Change Type -->
        <col style="width: 120px" />  <!-- Entity Type -->
        <col />                       <!-- Change Data -->
      </colgroup>

      <thead>
        <tr>
          <th>Serial</th>
          <th>Date</th>
          <th>User</th>
          <th>Change Type</th>
          <th>Entity Type</th>
          <th>Change Data</th>
        </tr>
      </thead>

      <tbody>
        <tr
          v-for="log in sortedChangeLogs"
          :key="log.id"
        >          <td>{{ log.serial }}</td>          <td>{{ formatDate(log.created_at) }}</td>
          <td>{{ log.username }}</td>
          <td>{{ log.operation }}</td>
          <td>{{ log.entity_type }}</td>
          <td class="change-data">{{ formatChangeData(log.change_data) }}</td>
        </tr>
      </tbody>
    </table>

    <div v-if="loading" class="loading-container">
      <div class="spinner"></div>
    </div>

    <div v-if="!loading && sortedChangeLogs.length === 0" class="empty-state">
      No change logs found
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface'
import { useToast } from 'vue-toast-notification'

const $toast = useToast()

const props = defineProps({
  datasetId: {
    type: Number,
    required: true
  }
})

const store = useStore()
const dsConn = new DatastoreConnect(store.state.enterpriseServerUrl)

const changeLogs = ref([])
const loading = ref(false)

const sortedChangeLogs = computed(() => {
  return [...changeLogs.value].sort((a, b) => {
    return new Date(b.created_at) - new Date(a.created_at)
  })
})

onMounted(() => {
  GetChangeLogs()
})

async function GetChangeLogs() {
  loading.value = true
  try {
    const response = await dsConn.api('version.changelog', {
      dataset_id: props.datasetId
    })
    
    if (response?.error) {
      $toast.open({
        queue: true,
        dismissible: true,
        message: `Failed to fetch change logs: ${response.error.message}`,
        type: 'error',
        position: 'top'
      })
      changeLogs.value = []
    } else {
      changeLogs.value = response?.result?.entries || []
    }
  } catch (error) {
    console.error('Error fetching change logs:', error)
    $toast.open({
      queue: true,
      dismissible: true,
      message: `Failed to fetch change logs: ${error}`,
      type: 'error',
      position: 'top'
    })
    changeLogs.value = []
  } finally {
    loading.value = false
  }
}

function formatDate(dateString) {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

function formatChangeData(data) {
  if (!data) return ''
  return JSON.stringify(data, null, 2)
}
</script>

<style lang="scss" scoped>
.change-logs {
  position: relative;
  height: 100%;
  overflow-y: auto;
  font-size: 12px;
}

.cl-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

/* Header */
.cl-table thead th {
  position: sticky;
  top: 0;
  background: #fafafa;
  z-index: 2;
  padding: 6px 8px;
  text-align: left;
  border-bottom: 1px solid #ccc;
  font-weight: 600;
}

/* Body */
.cl-table tbody td {
  padding: 6px 8px;
  border-bottom: 1px solid #eee;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cl-table tbody tr:hover {
  background: #f6f6f6;
}

.change-data {
  font-family: monospace;
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.empty-state {
  text-align: center;
  padding: 40px;
  color: #999;
  font-size: 0.9rem;
}
</style>
